import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Heading,
  Text,
  Box,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import EditableField from './EditableField';

function SOWModal({ isOpen, onClose, planData }) {
  const [sowData, setSowData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailsVisibility, setDetailsVisibility] = useState({});

  // Memoize fetchSOWData using useCallback
  const fetchSOWData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_PYTHON_API_URL}/generate-sow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(planData),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      const data = await response.json();
      setSowData(data);
    } catch (error) {
      console.error('Error fetching SOW data:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [planData]);

  useEffect(() => {
    if (isOpen) {
      fetchSOWData();
    }
  }, [isOpen, fetchSOWData]);

  // Function to handle editing fields
  const handleFieldChange = (fieldKey, value) => {
    setSowData((prevData) => ({
      ...prevData,
      [fieldKey]: value,
    }));
  };

  // Function to handle editing nested program fields
  const handleProgramFieldChange = (programIndex, fieldKey, value) => {
    setSowData((prevData) => {
      const updatedProgramOfWork = [...prevData.programOfWork];
      updatedProgramOfWork[programIndex] = {
        ...updatedProgramOfWork[programIndex],
        [fieldKey]: value,
      };
      return {
        ...prevData,
        programOfWork: updatedProgramOfWork,
      };
    });
  };

  // Function to toggle details visibility
  const toggleDetails = (index) => {
    setDetailsVisibility((prevVisibility) => ({
      ...prevVisibility,
      [index]: !prevVisibility[index],
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" p={10}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Statement of Work</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isLoading ? (
            <Flex justify="center" align="center" height="200px">
              <Spinner size="lg" />
            </Flex>
          ) : error ? (
            <Text color="red.500">Error: {error}</Text>
          ) : sowData ? (
            <VStack align="stretch" spacing={6}>
              <Heading size="lg">Purpose</Heading>
              <EditableField
                value={sowData.statementOfWorkIntro || ''}
                onChange={(value) => handleFieldChange('statementOfWorkIntro', value)}
                isMultiLine
              />

              <Heading size="lg">Design Challenge</Heading>
              <EditableField
                value={sowData.designChallenge || ''}
                onChange={(value) => handleFieldChange('designChallenge', value)}
                isMultiLine
              />

              <Heading size="lg">Problem Statement to Validate</Heading>
              <EditableField
                value={sowData.problemStatement || ''}
                onChange={(value) => handleFieldChange('problemStatement', value)}
                isMultiLine
              />

              <Heading size="lg">Business Vision & Objectives</Heading>
              <EditableField
                value={`${sowData.goalStatement || ''}\n${(sowData.objectivesStatement || []).join('\n')}`}
                onChange={(value) => {
                  const [goal, ...objectives] = value.split('\n');
                  handleFieldChange('goalStatement', goal);
                  handleFieldChange('objectivesStatement', objectives);
                }}
                isMultiLine
              />

              <Heading size="lg">Target</Heading>
              <EditableField
                value={sowData.targetStatement || ''}
                onChange={(value) => handleFieldChange('targetStatement', value)}
                isMultiLine
              />

              <Heading size="lg">Key Deliverables</Heading>
              <EditableField
                value={(sowData.deliverablesStatement || []).join('\n')}
                onChange={(value) => handleFieldChange('deliverablesStatement', value.split('\n'))}
                isMultiLine
              />

              <Heading size="lg">Scope of Work</Heading>
              {Array.isArray(sowData.programOfWork) && sowData.programOfWork.length > 0 ? (
                sowData.programOfWork.map((program, index) => (
                  <Box key={index} borderWidth="1px" borderRadius="md" p={4} mb={4}>
                    <Flex justify="space-between" align="center">
                      <Heading size="md">{program.programName}</Heading>
                      <Button
                        size="sm"
                        onClick={() => toggleDetails(index)}
                        colorScheme="teal"
                      >
                        {detailsVisibility[index] ? 'Hide Details' : 'View Details'}
                      </Button>
                    </Flex>
                    <Text mt={2} fontStyle="normal">
                      {program.goal || 'No objective provided.'}
                    </Text>
                    {detailsVisibility[index] && (
                      <Box mt={4}>
                        <Heading size="xs" mt={4}>Deliverables</Heading>
                        <EditableField
                          label="Expected Deliverables"
                          value={(program.expectedDeliverables || []).join('\n')}
                          onChange={(value) =>
                            handleProgramFieldChange(index, 'expectedDeliverables', value.split('\n'))
                          }
                          isMultiLine
                        />

                        <Heading size="xs" mt={4}>Stakeholders Involved</Heading>
                        <EditableField
                          label="Stakeholders & Customers Involvement"
                          value={(program.stakeholdersInvolvement || []).join('\n')}
                          onChange={(value) =>
                            handleProgramFieldChange(index, 'stakeholdersInvolvement', value.split('\n'))
                          }
                          isMultiLine
                        />

                        <Heading size="xs" mt={4}>Activities</Heading>
                        {Array.isArray(program.activities) && program.activities.length > 0 ? (
                          program.activities.map((activity, idx) => (
                            <Box key={idx} pl={4} mt={2} borderLeft="2px solid #E2E8F0">
                              <Flex direction="row" align="center" wrap="wrap">
                                <Text fontWeight="bold" mr={2}>
                                  {activity.name || 'Untitled'}:
                                </Text>
                                <Text flex="1 1 auto">
                                  {activity.description || 'No description provided.'}
                                </Text>
                              </Flex>
                            </Box>
                          ))
                        ) : (
                          <Text>No activities available.</Text>
                        )}
                      </Box>
                    )}
                  </Box>
                ))
              ) : (
                <Text>No Program of Work data available.</Text>
              )}

              <Heading size="lg">Stakeholders and Customers Involvement</Heading>
              <EditableField
                value={(sowData.stakeholdersCustomersInvolvement || []).join('\n')}
                onChange={(value) => handleFieldChange('stakeholdersCustomersInvolvement', value.split('\n'))}
                isMultiLine
              />

              <Heading size="lg">Timeline</Heading>
              {Array.isArray(sowData.timeline) && sowData.timeline.length > 0 ? (
                <Table size="md">
                  <Thead>
                    <Tr>
                      <Th>Program Name</Th>
                      <Th>Duration</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sowData.timeline.map((item, idx) => (
                      <Tr key={idx}>
                        <Td>{item.programName}</Td>
                        <Td>{item.duration}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                  <Thead>
                    <Tr>
                      <Th fontWeight="bold">Total Project Duration</Th>
                      <Td fontWeight="bold">{sowData.totalDuration}</Td>
                    </Tr>
                  </Thead>
                </Table>
              ) : (
                <Text>No Timeline data available.</Text>
              )}

              <Heading size="lg">Expected Metrics</Heading>
              <EditableField
                value={(sowData.metricsStatement || []).join('\n')}
                onChange={(value) => handleFieldChange('metricsStatement', value.split('\n'))}
                isMultiLine
              />

              {/*<Heading size="lg">Assumptions, Constraints, and Risks</Heading>
              <EditableField
                value={sowData.assumptionsConstraintsRisks || ''}
                onChange={(value) => handleFieldChange('assumptionsConstraintsRisks', value)}
                isMultiLine
              />*/}
            </VStack>
          ) : (
            <Text>No SOW data available.</Text>
          )}
        </ModalBody>
        <ModalFooter>
          {/*<Button onClick={onClose} colorScheme="blue">
            Close
          </Button>*/}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SOWModal;
